import { Icon } from "../components/common/Icon";
import { PageHero } from "../components/common/PageHero";
import { SmartLink } from "../components/common/SmartLink";
import { useDocumentMeta } from "../lib/useDocumentMeta";

const contactHref = "/contact";

const layers = [
  {
    icon: "brain" as const,
    title: "Policy-bound reasoning",
    copy: "Human leadership defines the rules, risk boundaries, and authorization expectations."
  },
  {
    icon: "shield" as const,
    title: "Controlled execution",
    copy: "RapidFactory and connected systems carry out approved work inside regulated operating paths."
  },
  {
    icon: "document" as const,
    title: "Defensible evidence",
    copy: "KyberDocs and the Holocron evidence model preserve what happened, why it happened, and what supports it."
  }
];

export function ControlPlanePage() {
  useDocumentMeta({
    canonicalPath: "/control-plane",
    description:
      "Holocron is the authority layer behind Conscious Security for machine-speed, policy-bound defense and operations.",
    title: "Control Plane | Holocron Security"
  });

  return (
    <>
      <PageHero
        actions={[
          { href: "/solutions", label: "Explore Solutions", variant: "primary" },
          { href: contactHref, label: "Plan AI Implementation", variant: "secondary" }
        ]}
        copy="The control plane binds AI, infrastructure, remediation, and evidence to policy before regulated operations drift out of control."
        eyebrow="Architecture"
        title="Authority for machine-speed operations."
      />

      <section className="surface-dark surface-dark--control">
        <div className="section-shell">
          <div className="overview-card">
            <p className="section-kicker">What Holocron provides</p>
            <h2 className="section-title">Fast systems still need explicit authority.</h2>
            <div className="stack-copy">
              <p>
                Holocron sits above implementation services, environment automation, security tooling, and evidence
                workflows so regulated organizations can keep decisions policy-bound and reconstructable.
              </p>
              <p>
                The website leads with practical services because that is where buyers start. The control plane is the
                architecture that keeps those services coherent as systems change.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="surface-gradient surface-gradient--control">
        <div className="section-shell">
          <div className="use-case-grid">
            {layers.map((card) => (
              <article key={card.title} className="use-case-card">
                <div className="icon-badge">
                  <Icon name={card.icon} />
                </div>
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
          <p className="section-emphasis section-emphasis--center">
            Conscious Security means machine-speed, policy-bound defense and operations.
          </p>
        </div>
      </section>

      <section className="surface-dark cta-band">
        <div className="section-shell cta-center">
          <h2 className="section-title section-title--center">
            Turn the architecture into implementation work.
          </h2>
          <p>
            Start with secure AI implementation, a CMMC Level 2 environment, or the RapidFactory + KyberDocs platform
            workflow.
          </p>
          <div className="hero-actions hero-actions--centered">
            <SmartLink className="button primary" href="/solutions">
              View Solutions
            </SmartLink>
            <SmartLink className="button secondary" href="/rapid-factory">
              Explore RapidFactory
            </SmartLink>
          </div>
        </div>
      </section>
    </>
  );
}
